import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { Download, KeyRound, LogOut, Menu, UserCircle2, X } from 'lucide-react';
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@fas/ui';
import { useAuthStore } from '@/lib/auth-store';
import { initials } from '@/lib/format';
import { promptInstall, usePwaStore } from '@/lib/pwa';
import { useRealtimeConnection } from '@/lib/realtime';
import { signOut } from '@/lib/session';
import { useApprovalCount, useApprovalLiveRefresh } from '@/features/requests/hooks';
import { NAV_ITEMS, type NavItem, visibleNavItems } from './nav';
import { NotificationsBell } from './NotificationsBell';
import { OfflineQueueStatus } from './OfflineQueueStatus';
import { PwaStatus } from './PwaStatus';

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const approvals = useApprovalCount(items.some((i) => i.badge === 'approvals'));
  const pending = approvals.data?.pending ?? 0;
  const sections: Array<[string, NavItem[]]> = [
    ['Main', items.filter((i) => i.section === 'main')],
    ['Administration', items.filter((i) => i.section === 'admin')],
  ];
  return (
    <nav aria-label="Primary" className="space-y-6">
      {sections
        .filter(([, list]) => list.length > 0)
        .map(([title, list]) => (
          <div key={title}>
            <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
            <ul className="space-y-1">
              {list.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                        isActive ? 'bg-blue-50 text-blue-800' : 'text-slate-700 hover:bg-slate-100',
                      )
                    }
                  >
                    <item.icon className="size-4" aria-hidden />
                    {item.label}
                    {item.badge === 'approvals' && pending > 0 ? (
                      <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                        {pending > 99 ? '99+' : pending}
                        <span className="sr-only"> waiting for your decision</span>
                      </span>
                    ) : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </nav>
  );
}

export function AppShell() {
  const me = useAuthStore((s) => s.me);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = visibleNavItems(NAV_ITEMS, me);
  const installPrompt = usePwaStore((s) => s.installPrompt);
  const name = me?.faculty?.fullName ?? me?.user.displayName ?? me?.user.email ?? '';
  useRealtimeConnection();
  // Keeps the approvals badge and any open request views current.
  useApprovalLiveRefresh(1_500);

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-dvh">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Skip to main content
      </a>
      <div className="sticky top-0 z-30 print:hidden">
        <header className="flex h-14 items-center gap-3 border-b border-slate-200 bg-white/95 px-3 backdrop-blur sm:px-4">
          <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
            <DialogPrimitive.Trigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open navigation">
                <Menu aria-hidden />
              </Button>
            </DialogPrimitive.Trigger>
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-slate-950/40 lg:hidden" />
              <DialogPrimitive.Content className="fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-white p-4 shadow-xl focus:outline-none lg:hidden">
                <div className="mb-4 flex items-center justify-between">
                  <DialogPrimitive.Title className="font-semibold">Navigation</DialogPrimitive.Title>
                  <DialogPrimitive.Close asChild>
                    <Button variant="ghost" size="icon" aria-label="Close navigation">
                      <X aria-hidden />
                    </Button>
                  </DialogPrimitive.Close>
                </div>
                <DialogPrimitive.Description className="sr-only">Application sections</DialogPrimitive.Description>
                <NavList items={items} onNavigate={() => setOpen(false)} />
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>

          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="grid size-8 place-items-center rounded-md bg-blue-700 text-sm font-bold text-white" aria-hidden>
              FA
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-tight">Faculty Attendance</p>
              <p className="truncate text-xs text-slate-500">{me?.institution?.name}</p>
            </div>
          </div>

          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2" aria-label="Account menu">
                <span className="grid size-8 place-items-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700">
                  {initials(name) || '?'}
                </span>
                <span className="hidden max-w-40 truncate text-sm sm:inline">{name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>{me?.user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/account')}>
                <UserCircle2 className="size-4" aria-hidden /> My account
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/account#security')}>
                <KeyRound className="size-4" aria-hidden /> Password & sessions
              </DropdownMenuItem>
              {installPrompt ? (
                <DropdownMenuItem onSelect={() => void promptInstall()}>
                  <Download className="size-4" aria-hidden /> Install app
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleSignOut}>
                <LogOut className="size-4" aria-hidden /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <PwaStatus />
        <OfflineQueueStatus />
      </div>

      <div className="mx-auto flex max-w-screen-2xl">
        <aside className="sticky top-14 hidden h-[calc(100dvh-3.5rem)] w-64 shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-4 lg:block print:hidden">
          <NavList items={items} />
        </aside>
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 p-4 focus:outline-none sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
